import { sessionStorage } from 'dibs-browser-storage';
import globalNotifications from './globalNotifications';

export const KEY_DISABLE_NOTIFICATIONS = 'no-notif';
export const KEY_ISOLATE_NOTIFICATION = 'isolate-notification';
// useful if you have specific conditions that are very manual to trigger
// ie. trade level up notification modal requires the following:
// - transact and confirm a purchase for a trade firm that will bump
//   the firm into a different trade tier (member -> bronze, bronze -> silver, etc)
// this should only be used for testing purposes and must be used with `KEY_ISOLATE_NOTIFICATION`
const KEY_FORCE_NOTIFICATION = 'force-notif';

import { shouldSkipNotifForTrade, incrementTradeNotifStorage } from './tradeHelpers';

const NO_OP_ACTION_TYPE = { actionType: 'noop' };

let hasBeenInitialized = false;
let queue;

function createStackTrace(msg) {
    const err = new Error(msg);
    if (typeof Error.captureStackTrace === 'function') {
        Error.captureStackTrace(err, createStackTrace);
    }
    if (err.stack) {
        return err.stack;
    }
    return err;
}

export default class Queue {
    constructor(notifications, store, relayEnvironment, autoRun = true, customActionTypes = {}) {
        this.store = store;
        this.relayEnvironment = relayEnvironment;
        // allows a way for the consumer to hook into the global
        // notification queue when its action has been executed
        this.customActionTypes = customActionTypes;
        // should onl be used for testing purpsose
        // see comment for `KEY_FORCE_NOTIFICATION`
        this.forceNotification = false;

        if (!hasBeenInitialized) {
            queue = this.buildQueue(notifications);
            hasBeenInitialized = true;

            if (autoRun) {
                this.runQueue();
            }
        } else {
            throw new Error('Only one notification queue may be initialized');
        }
    }

    buildQueue(notifications) {
        const query = new URLSearchParams(window.location.search);
        const isolatedNotification = query.get(KEY_ISOLATE_NOTIFICATION);
        const fullQueue = [].concat(globalNotifications, notifications).sort((a, b) => {
            return b.priority - a.priority;
        });
        if (isolatedNotification) {
            const isolatedNotificationConfig = fullQueue.find(
                item => item.name === isolatedNotification
            );
            if (!isolatedNotificationConfig) {
                console.warn(`${isolatedNotification} not found in queue`);
                return [];
            }
            const forceNotification = query.get(KEY_FORCE_NOTIFICATION);
            if (forceNotification === 'true') {
                this.forceNotification = true;
            }
            return [isolatedNotificationConfig];
        }
        if (this.noNotifications(query)) {
            return [];
        }
        if (shouldSkipNotifForTrade()) {
            return [];
        }

        return fullQueue;
    }

    noNotifications(query) {
        if (sessionStorage.getItem(KEY_DISABLE_NOTIFICATIONS) === true) {
            return true;
        }
        if (query.has(KEY_DISABLE_NOTIFICATIONS)) {
            sessionStorage.setItem(KEY_DISABLE_NOTIFICATIONS, true);
            return true;
        }

        return false;
    }

    _getNotificationQueue() {
        if (process.env.NODE_ENV === 'production') {
            console.error('this method is for development purposes only');
        }
        return queue;
    }

    async runQueue(index = 0) {
        if (index < queue.length) {
            const notification = queue[index];
            const showNotification = await notification.rule({
                relayEnvironment: this.relayEnvironment,
                forceNotification: this.forceNotification,
            });
            if (showNotification) {
                const notificationName = notification.name;
                const customActionType =
                    this.customActionTypes && this.customActionTypes[notificationName];
                // object if we want to potentially expand on this concept
                const notifArgs = customActionType
                    ? { customActionType }
                    : { customActionType: NO_OP_ACTION_TYPE };
                if (
                    customActionType &&
                    window.newrelic &&
                    typeof window.newrelic.addPageAction === 'function'
                ) {
                    window.newrelic.addPageAction('dbl:audit', {
                        notificationName,
                        customActionType,
                        stack: createStackTrace(`${notificationName} called`),
                    });
                }
                incrementTradeNotifStorage();
                return this.store.dispatch(notification.action(notifArgs));
            } else {
                return await this.runQueue(++index);
            }
        } else {
            return Promise.resolve();
        }
    }
}

export const _testReset = () => {
    hasBeenInitialized = false;
};
